/**
 * ASCII Hero Engine - AsciiBlurRectangle Class
 * Real-time hardware-accelerated canvas backdrop blur card with rounded corners,
 * frosted glass tint, crisp vector glass borders, 3D parallax depth and spotlight response.
 */

import { Easings } from "../utils/easings.js";

export class AsciiBlurRectangle {
  constructor(options = {}) {
    this.name = options.name || "blur-rectangle";
    this.type = "blur-rectangle";

    // Positioning: 'portrait' (anchored to photo) or 'screen' (anchored to viewport 0..1)
    this.anchor = options.anchor || "portrait";
    this.u = options.u !== undefined ? options.u : 0.65;
    this.v = options.v !== undefined ? options.v : 0.32;
    this.normalized = options.normalized !== undefined ? options.normalized : true;
    this.x = options.x || 0;
    this.y = options.y || 0;
    this.worldX = 0;
    this.worldY = 0;

    // Dimensions (normalized w, h or pixel width, height)
    this.width = options.width || 280;
    this.height = options.height || 180;
    this.w = options.w !== undefined ? options.w : null;
    this.h = options.h !== undefined ? options.h : null;

    // Glass styling
    this.borderRadius = options.borderRadius !== undefined ? options.borderRadius : 16;
    this.blur = options.blur !== undefined ? options.blur : 16; // Blur intensity in px
    this.tint = options.tint || "rgba(255, 255, 255, 0.04)"; // Frosted glass background tint
    this.borderColor = options.borderColor || "rgba(255, 255, 255, 0.18)"; // Glass border
    this.borderWidth = options.borderWidth !== undefined ? options.borderWidth : 1;
    this.opacity = options.opacity !== undefined ? options.opacity : 1.0;

    // 3D Parallax & Layering
    this.depth = options.depth !== undefined ? options.depth : 0.6;
    this.rotation = options.rotation || 0;
    this.layer = options.layer || "foreground"; // 'foreground' or 'background'

    // Spotlight response
    this.spotlightIntensity = 0;
    this.spotlightRadius = options.spotlightRadius || 260;
    this.spotlightHighlight = options.spotlightHighlight !== undefined ? options.spotlightHighlight : true;
  }

  update(mouse, layout, config, parallaxX = 0, parallaxY = 0) {
    if (this.targetOpacity !== undefined && this.opacity !== this.targetOpacity) {
      this.opacity += (this.targetOpacity - this.opacity) * (this.fadeSpeed || 0.08);
      if (Math.abs(this.opacity - this.targetOpacity) < 0.005) {
        this.opacity = this.targetOpacity;
      }
    }

    const screenW = (config && config.width) || (typeof window !== "undefined" ? window.innerWidth : 1920);
    const screenH = (config && config.height) || (typeof window !== "undefined" ? window.innerHeight : 1080);

    // Compute world position based on layout or canvas
    if (this.anchor === "screen") {
      this.worldX = this.u * screenW;
      this.worldY = this.v * screenH;
    } else if (this.normalized && layout && layout.renderW > 0) {
      this.worldX = layout.posX + this.u * layout.renderW;
      this.worldY = layout.posY + this.v * layout.renderH;
    } else if (this.x !== 0 || this.y !== 0) {
      this.worldX = this.x;
      this.worldY = this.y;
    } else {
      this.worldX = this.u * screenW;
      this.worldY = this.v * screenH;
    }

    // Effective screen position with 3D parallax
    const drawX = this.worldX + parallaxX * this.depth;
    const drawY = this.worldY + parallaxY * this.depth;
    const renderW = this.w && layout ? this.w * layout.renderW : this.width;
    const renderH = this.h && layout ? this.h * layout.renderH : this.height;

    // Center of rectangle for distance calculations
    const centerX = drawX + renderW * 0.5;
    const centerY = drawY + renderH * 0.5;

    const dx = centerX - (mouse ? mouse.x : -9999);
    const dy = centerY - (mouse ? mouse.y : -9999);
    const dist = mouse && mouse.x !== -9999 ? Math.sqrt(dx * dx + dy * dy) : 99999;

    const sRadius = this.spotlightRadius || (config && config.spotlightRadius) || 260;
    let targetSpotlight = 0;
    if (mouse && mouse.x !== -9999 && dist < sRadius) {
      const normDist = dist / sRadius;
      targetSpotlight = Easings.easeOutQuart(1 - normDist);
    }

    if (targetSpotlight > this.spotlightIntensity) {
      this.spotlightIntensity += (targetSpotlight - this.spotlightIntensity) * 0.16;
    } else {
      this.spotlightIntensity += (targetSpotlight - this.spotlightIntensity) * 0.048;
    }
  }

  draw(ctx, config, parallaxX = 0, parallaxY = 0, layout = null, blurCanvas = null) {
    const itemOpacity = this.opacity !== undefined ? this.opacity : 1.0;
    if (itemOpacity <= 0.001) return;

    let cfg = config;
    let pX = parallaxX;
    let pY = parallaxY;
    let lay = layout;
    let bCanvas = blurCanvas;

    if (config && (config instanceof HTMLCanvasElement || config.getContext)) {
      bCanvas = config;
      cfg = arguments[2] || {};
      pX = arguments[3] || 0;
      pY = arguments[4] || 0;
      lay = arguments[5] || null;
    }

    pX = typeof pX === "number" && !isNaN(pX) ? pX : 0;
    pY = typeof pY === "number" && !isNaN(pY) ? pY : 0;

    const dpr = (cfg && cfg.dpr) || (typeof window !== "undefined" ? window.devicePixelRatio : 1) || 1;
    const basePosX = typeof this.worldX === "number" && !isNaN(this.worldX) ? this.worldX : 0;
    const basePosY = typeof this.worldY === "number" && !isNaN(this.worldY) ? this.worldY : 0;
    const drawX = basePosX + pX * (this.depth || 0);
    const drawY = basePosY + pY * (this.depth || 0);
    const renderW = this.w && lay ? this.w * lay.renderW : this.width;
    const renderH = this.h && lay ? this.h * lay.renderH : this.height;

    // Helper to create rounded rectangle path in CSS coordinates
    const makePath = (c) => {
      c.beginPath();
      if (typeof c.roundRect === "function") {
        c.roundRect(drawX, drawY, renderW, renderH, this.borderRadius);
      } else {
        const r = typeof this.borderRadius === "number" ? this.borderRadius : 12;
        c.moveTo(drawX + r, drawY);
        c.arcTo(drawX + renderW, drawY, drawX + renderW, drawY + renderH, r);
        c.arcTo(drawX + renderW, drawY + renderH, drawX, drawY + renderH, r);
        c.arcTo(drawX, drawY + renderH, drawX, drawY, r);
        c.arcTo(drawX, drawY, drawX + renderW, drawY, r);
        c.closePath();
      }
    };

    // 1. HARDWARE-ACCELERATED BACKDROP BLUR PASS
    if (bCanvas && this.blur > 0) {
      ctx.save();
      makePath(ctx);
      ctx.clip();

      ctx.globalAlpha = ctx.globalAlpha * itemOpacity;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.filter = `blur(${this.blur * dpr}px)`;
      ctx.drawImage(bCanvas, 0, 0);
      ctx.filter = "none";
      ctx.restore();
    }

    // 2. FROSTED GLASS TINT FILL
    ctx.save();
    makePath(ctx);
    ctx.globalAlpha = ctx.globalAlpha * itemOpacity;
    ctx.fillStyle = this.tint;
    ctx.fill();

    if (this.spotlightHighlight && this.spotlightIntensity > 0.01) {
      ctx.fillStyle = `rgba(255, 255, 255, ${this.spotlightIntensity * 0.06})`;
      ctx.fill();
    }
    ctx.restore();

    // 3. CRISP GLASS BORDER
    if (this.borderWidth > 0) {
      ctx.save();
      makePath(ctx);
      ctx.globalAlpha = ctx.globalAlpha * itemOpacity;
      ctx.lineWidth = this.borderWidth;

      if (this.spotlightHighlight && this.spotlightIntensity > 0.01) {
        ctx.strokeStyle = `rgba(255, 255, 255, ${Math.min(0.85, 0.18 + this.spotlightIntensity * 0.45)})`;
      } else {
        ctx.strokeStyle = this.borderColor;
      }

      ctx.stroke();
      ctx.restore();
    }
  }
}
